import {alias, createModelSchema, custom, deserialize, list, serialize, SKIP} from "serializr";
import {Schema} from "@/common/Schema";
import {Cloneable} from "@/common/Cloneable";
import {Range} from "../utils/Range";
import {GuardRule} from "./GuardRule";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";
import {ProductStatus} from "@/modules/redactor/models/product/ProductStatus";
import {AvatarStatus} from "@/modules/redactor/models/avatar/AvatarStatus";


export class GuardConfig extends Cloneable {

    static schema() {
        return custom(
            (v) => {
                return v && !v.empty() ? serialize(GuardConfig, v) : SKIP;
            },
            (v) => {
                return deserialize(GuardConfig, v);
            }
        );
    }

    constructor() {
        super();

        this._level = new Range();

        this._allow = [];
        this._block = [];

        this._allowAnyOf = false;
        this._blockAnyOf = false;

        this._offersAllow = [];
        this._offersBlock = [];

        this._avatar = null;
        this._avatarStatus = AvatarStatus.LOCKED;

        this._product = null;
        this._productStatus = ProductStatus.NONE;
    }

    get level() {
        return this._level;
    }

    set level(value) {
        this._level = value;
    }

    get allow() {
        return this._allow;
    }

    set allow(value) {
        this._allow = value;
    }

    get block() {
        return this._block;
    }

    set block(value) {
        this._block = value;
    }

    get allowAnyOf() {
        return this._allowAnyOf;
    }

    set allowAnyOf(value) {
        this._allowAnyOf = value;
    }

    get blockAnyOf() {
        return this._blockAnyOf;
    }

    set blockAnyOf(value) {
        this._blockAnyOf = value;
    }

    get offersAllow() {
        return this._offersAllow;
    }

    set offersAllow(value) {
        this._offersAllow = value;
    }

    get offersBlock() {
        return this._offersBlock;
    }

    set offersBlock(value) {
        this._offersBlock = value;
    }

    get avatar() {
        return this._avatar;
    }

    set avatar(value) {
        this._avatar = value;
    }

    get avatarStatus() {
        return this._avatarStatus;
    }

    set avatarStatus(value) {
        this._avatarStatus = value;
    }

    get product() {
        return this._product;
    }

    set product(value) {
        this._product = value;
    }

    get productStatus() {
        return this._productStatus;
    }

    set productStatus(value) {
        this._productStatus = value;
    }

    valid() {

        if (this.level && !this.level.valid()) {
            return false;
        }

        if (this.allow && this.allow.some(v => !v || !v.valid())) {
            return false;
        }

        if (this.block && this.block.some(v => !v || !v.valid())) {
            return false;
        }

        if (this.offersAllow && this.offersAllow.some(v => !RedactorUtils.getSmartOffer(v))) {
            return false;
        }

        if (this.offersBlock && this.offersBlock.some(v => !RedactorUtils.getSmartOffer(v))) {
            return false;
        }

        if (this.product && ProductStatus.isNone(this.productStatus)) {
            return false;
        }

        return true;
    }

    empty() {

        if (this.level && !this.level.empty()) {
            return false;
        } else if (this.allow && this.allow.length > 0) {
            return false;
        } else if (this.block && this.block.length > 0) {
            return false;
        } else if (this.offersAllow && this.offersAllow.length > 0) {
            return false;
        } else if (this.offersBlock && this.offersBlock.length > 0) {
            return false;
        } else if (this.product) {
            return false;
        }

        return true;
    }

    clone(target = undefined) {
        if (!target) {
            return super.clone(new GuardConfig());
        } else if (target instanceof GuardConfig) {
            return super.clone(target);
        } else {
            console.assert(false);
            return undefined;
        }
    }
}

createModelSchema(GuardConfig, {
    _level: alias("level", Range.schema()),
    _allow: alias("allow", list(GuardRule.schema())),
    _block: alias("block", list(GuardRule.schema())),

    _allowAnyOf: alias("allow_anyof", Schema.primitive()),
    _blockAnyOf: alias("block_anyof", Schema.primitive()),

    _offersAllow: alias("offers_allow", list(Schema.primitive())),
    _offersBlock: alias("offers_block", list(Schema.primitive())),

    _avatar: alias("avatar_uid", Schema.primitive()),
    _avatarStatus: alias("avatar_status", AvatarStatus.schema()),

    _product: alias("product_uid", Schema.primitive()),
    _productStatus: alias("product_status", ProductStatus.schema())
});